import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SearchIcon, UsersIcon, SendIcon, CheckCircle2Icon, MailIcon, PhoneIcon, BuildingIcon } from 'lucide-react';
type Step = 'search' | 'contacts' | 'sequence';
export function DatabaseDemo() {
  const [activeStep, setActiveStep] = useState<Step>('search');
  const [searchPhase, setSearchPhase] = useState<'loading' | 'done'>('loading');
  const [contactsPhase, setContactsPhase] = useState<'loading' | 'done'>('loading');
  const [sequencePhase, setSequencePhase] = useState<'loading' | 'done'>('loading');
  const searchTimerRef = useRef<NodeJS.Timeout | null>(null);
  const contactsTimerRef = useRef<NodeJS.Timeout | null>(null);
  const sequenceTimerRef = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    startSearch();
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
      if (contactsTimerRef.current) clearTimeout(contactsTimerRef.current);
      if (sequenceTimerRef.current) clearTimeout(sequenceTimerRef.current);
    };
  }, []);
  const startSearch = () => {
    setSearchPhase('loading');
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => setSearchPhase('done'), 1200);
  };
  const startContacts = () => {
    setContactsPhase('loading');
    if (contactsTimerRef.current) clearTimeout(contactsTimerRef.current);
    contactsTimerRef.current = setTimeout(() => setContactsPhase('done'), 1400);
  };
  const startSequence = () => {
    setSequencePhase('loading');
    if (sequenceTimerRef.current) clearTimeout(sequenceTimerRef.current);
    sequenceTimerRef.current = setTimeout(() => setSequencePhase('done'), 1500);
  };
  const handleTabSwitch = (step: Step) => {
    setActiveStep(step);
    if (step === 'search') startSearch();
    if (step === 'contacts') startContacts();
    if (step === 'sequence') startSequence();
  };
  return <section className="py-24 bg-white" data-id="element-2098">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2099">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-2100">
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6 leading-tight" data-id="element-2101">
            Experience the{' '}
            <span className="text-blue-600" data-id="element-2102">local business database</span>
          </h2>
          <p className="text-xl text-slate-600 leading-relaxed" data-id="element-2103">
            Search, view employee email lists, and launch sequences without ever
            leaving the platform.
          </p>
        </div>

        <div className="flex flex-wrap justify-center gap-3 mb-14" data-id="element-2104">
          <button onClick={() => handleTabSwitch('search')} className={`flex items-center gap-2 px-6 py-3 rounded-full text-base font-semibold transition-all duration-200 ${activeStep === 'search' ? 'bg-slate-900 text-white' : 'bg-slate-50 text-slate-600 hover:bg-slate-100'}`} data-id="element-2105">
            <SearchIcon className="w-4 h-4" data-id="element-2106" />
            Search local businesses
          </button>
          <button onClick={() => handleTabSwitch('contacts')} className={`flex items-center gap-2 px-6 py-3 rounded-full text-base font-semibold transition-all duration-200 ${activeStep === 'contacts' ? 'bg-slate-900 text-white' : 'bg-slate-50 text-slate-600 hover:bg-slate-100'}`} data-id="element-2107">
            <UsersIcon className="w-4 h-4" data-id="element-2108" />
            View owner emails
          </button>
          <button onClick={() => handleTabSwitch('sequence')} className={`flex items-center gap-2 px-6 py-3 rounded-full text-base font-semibold transition-all duration-200 ${activeStep === 'sequence' ? 'bg-slate-900 text-white' : 'bg-slate-50 text-slate-600 hover:bg-slate-100'}`} data-id="element-2109">
            <SendIcon className="w-4 h-4" data-id="element-2110" />
            Launch sequence
          </button>
        </div>

        <AnimatePresence mode="wait" data-id="element-2111">
          {activeStep === 'search' && <motion.div key="search" initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.25
        }} className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start" data-id="element-2112">
              <div className="lg:col-span-4 bg-slate-50 rounded-[2.5rem] p-8 sm:p-10" data-id="element-2113">
                <h3 className="text-lg font-bold text-slate-900 mb-1" data-id="element-2114">
                  Search local businesses
                </h3>
                <p className="text-sm text-slate-500 mb-8" data-id="element-2115">
                  Query our local business database by industry and location.
                </p>
                <div className="space-y-5" data-id="element-2116">
                  <div data-id="element-2117">
                    <label className="block text-sm font-semibold text-slate-700 mb-1.5" data-id="element-2118">
                      Industry
                    </label>
                    <div className="w-full px-5 py-3.5 bg-white rounded-2xl text-base shadow-sm text-slate-700" data-id="element-2119">
                      Dental Clinics
                    </div>
                  </div>
                  <div data-id="element-2120">
                    <label className="block text-sm font-semibold text-slate-700 mb-1.5" data-id="element-2121">
                      Location
                    </label>
                    <div className="w-full px-5 py-3.5 bg-white rounded-2xl text-base shadow-sm text-slate-700" data-id="element-2122">
                      California
                    </div>
                  </div>
                </div>
                <div className="mt-8 flex items-center justify-center gap-2 py-4 text-sm font-semibold" data-id="element-2123">
                  {searchPhase === 'loading' ? <span className="flex items-center gap-2 text-blue-600" data-id="element-2124">
                      <motion.div animate={{
                  rotate: 360
                }} transition={{
                  repeat: Infinity,
                  duration: 1,
                  ease: 'linear'
                }} className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full" data-id="element-2125" />
                      Searching...
                    </span> : <span className="flex items-center gap-2 text-green-600" data-id="element-2126">
                      <CheckCircle2Icon className="w-4 h-4" data-id="element-2127" />
                      4,210 businesses found
                    </span>}
                </div>
              </div>
              <div className="lg:col-span-8 bg-slate-50 rounded-[2.5rem] p-8 sm:p-10 min-h-[420px]" data-id="element-2128">
                {searchPhase === 'loading' && <div className="h-full min-h-[350px] flex flex-col items-center justify-center" data-id="element-2129">
                    <motion.div animate={{
                rotate: 360
              }} transition={{
                repeat: Infinity,
                duration: 1,
                ease: 'linear'
              }} className="w-10 h-10 rounded-full mb-4" style={{
                borderWidth: '3px',
                borderColor: '#e2e8f0',
                borderTopColor: '#156ef6',
                borderStyle: 'solid'
              }} data-id="element-2130" />
                    <p className="text-lg font-medium text-slate-500" data-id="element-2131">
                      Querying database...
                    </p>
                  </div>}
                {searchPhase === 'done' && <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              duration: 0.4
            }} data-id="element-2132">
                    <div className="flex items-center justify-between mb-6" data-id="element-2133">
                      <h3 className="text-lg font-bold text-slate-900" data-id="element-2134">
                        Results
                      </h3>
                      <span className="text-sm font-semibold text-blue-600 bg-blue-50 px-3 py-1 rounded-full" data-id="element-2135">
                        4,210 found
                      </span>
                    </div>
                    <div className="space-y-3" data-id="element-2136">
                      {[{
                  name: 'Smile Bright Dental',
                  location: 'San Francisco, CA',
                  employees: '15',
                  rating: '4.8',
                  phone: '(415) 555-0123',
                  email: 'info@smilebright.com',
                  category: 'Dentist',
                  revenue: '$1.2M'
                }, {
                  name: 'Cali Coast Dentistry',
                  location: 'Los Angeles, CA',
                  employees: '28',
                  rating: '4.6',
                  phone: '(310) 555-0456',
                  email: 'hello@calicoast.com',
                  category: 'Dentist',
                  revenue: '$2.1M'
                }, {
                  name: 'Bay Area Orthodontics',
                  location: 'Oakland, CA',
                  employees: '12',
                  rating: '4.9',
                  phone: '(510) 555-0789',
                  email: 'contact@bayortho.com',
                  category: 'Orthodontist',
                  revenue: '$900K'
                }].map((biz, idx) => <motion.div key={idx} initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  delay: idx * 0.15,
                  duration: 0.3
                }} className="bg-white rounded-2xl p-5 shadow-sm" data-id="element-2137">
                          <div className="flex items-start justify-between mb-3" data-id="element-2138">
                            <div data-id="element-2139">
                              <h4 className="text-base font-bold text-slate-900" data-id="element-2140">
                                {biz.name}
                              </h4>
                              <div className="flex items-center gap-3 mt-1" data-id="element-2141">
                                <span className="text-xs text-slate-500" data-id="element-2142">
                                  {biz.location}
                                </span>
                                <span className="text-xs text-slate-500" data-id="element-2143">
                                  ⭐ {biz.rating}
                                </span>
                                <span className="text-xs text-slate-400" data-id="element-2144">
                                  {biz.category}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-xs text-slate-600" data-id="element-2145">
                            <span className="flex items-center gap-1.5" data-id="element-2146">
                              <MailIcon className="w-3.5 h-3.5 text-blue-400" data-id="element-2147" />
                              {biz.email}
                            </span>
                            <span className="flex items-center gap-1.5" data-id="element-2148">
                              <PhoneIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-2149" />
                              {biz.phone}
                            </span>
                            <span className="text-slate-400" data-id="element-2150">•</span>
                            <span data-id="element-2151">{biz.employees} employees</span>
                            <span className="text-slate-400" data-id="element-2152">•</span>
                            <span data-id="element-2153">{biz.revenue} est. revenue</span>
                          </div>
                        </motion.div>)}
                    </div>
                  </motion.div>}
              </div>
            </motion.div>}

          {activeStep === 'contacts' && <motion.div key="contacts" initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.25
        }} className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start" data-id="element-2154">
              <div className="lg:col-span-4 bg-slate-50 rounded-[2.5rem] p-8 sm:p-10" data-id="element-2155">
                <h3 className="text-lg font-bold text-slate-900 mb-1" data-id="element-2156">
                  View owner & decision-maker emails
                </h3>
                <p className="text-sm text-slate-500 mb-8" data-id="element-2157">
                  Access local business employee email lists.
                </p>
                <div className="space-y-5" data-id="element-2158">
                  <div data-id="element-2159">
                    <label className="block text-sm font-semibold text-slate-700 mb-1.5" data-id="element-2160">
                      Target Titles
                    </label>
                    <div className="w-full px-5 py-3.5 bg-white rounded-2xl text-base shadow-sm text-slate-700" data-id="element-2161">
                      Owner, Dentist, Founder
                    </div>
                  </div>
                </div>
                <div className="mt-8 flex items-center justify-center gap-2 py-4 text-sm font-semibold" data-id="element-2162">
                  {contactsPhase === 'loading' ? <span className="flex items-center gap-2 text-blue-600" data-id="element-2163">
                      <motion.div animate={{
                  rotate: 360
                }} transition={{
                  repeat: Infinity,
                  duration: 1,
                  ease: 'linear'
                }} className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full" data-id="element-2164" />
                      Loading contacts...
                    </span> : <span className="flex items-center gap-2 text-green-600" data-id="element-2165">
                      <CheckCircle2Icon className="w-4 h-4" data-id="element-2166" />
                      Emails unlocked
                    </span>}
                </div>
              </div>
              <div className="lg:col-span-8 bg-slate-50 rounded-[2.5rem] p-8 sm:p-10 min-h-[420px]" data-id="element-2167">
                {contactsPhase === 'loading' && <div className="h-full min-h-[350px] flex flex-col items-center justify-center" data-id="element-2168">
                    <motion.div animate={{
                rotate: 360
              }} transition={{
                repeat: Infinity,
                duration: 1,
                ease: 'linear'
              }} className="w-10 h-10 rounded-full mb-4" style={{
                borderWidth: '3px',
                borderColor: '#e2e8f0',
                borderTopColor: '#156ef6',
                borderStyle: 'solid'
              }} data-id="element-2169" />
                    <p className="text-lg font-medium text-slate-500" data-id="element-2170">
                      Fetching employee emails...
                    </p>
                  </div>}
                {contactsPhase === 'done' && <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              duration: 0.4
            }} data-id="element-2171">
                    <div className="flex items-center justify-between mb-6" data-id="element-2172">
                      <h3 className="text-lg font-bold text-slate-900" data-id="element-2173">
                        Local business contacts
                      </h3>
                    </div>
                    <div className="space-y-3" data-id="element-2174">
                      {[{
                  name: 'Dr. Emily Chen',
                  role: 'Owner / Lead Dentist',
                  email: 'emily@smilebright.com'
                }, {
                  name: 'Marcus Johnson',
                  role: 'Founder',
                  email: 'mjohnson@calicoast.com'
                }, {
                  name: 'Sarah Williams',
                  role: 'Practice Manager',
                  email: 'sarah.w@bayareaortho.com'
                }].map((contact, idx) => <motion.div key={idx} initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  delay: idx * 0.15,
                  duration: 0.3
                }} className="bg-white rounded-2xl p-5 shadow-sm flex items-center justify-between" data-id="element-2175">
                          <div data-id="element-2176">
                            <h4 className="text-base font-bold text-slate-900" data-id="element-2177">
                              {contact.name}
                            </h4>
                            <p className="text-xs text-slate-500" data-id="element-2178">
                              {contact.role}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-blue-600 bg-blue-50 px-3 py-1.5 rounded-lg font-medium" data-id="element-2179">
                            <MailIcon className="w-4 h-4" data-id="element-2180" />
                            {contact.email}
                          </div>
                        </motion.div>)}
                    </div>
                  </motion.div>}
              </div>
            </motion.div>}

          {activeStep === 'sequence' && <motion.div key="sequence" initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.25
        }} className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start" data-id="element-2181">
              <div className="lg:col-span-4 bg-slate-50 rounded-[2.5rem] p-8 sm:p-10" data-id="element-2182">
                <h3 className="text-lg font-bold text-slate-900 mb-1" data-id="element-2183">
                  Launch email sequence
                </h3>
                <p className="text-sm text-slate-500 mb-8" data-id="element-2184">
                  Send emails directly to local business contacts.
                </p>
                <div className="space-y-5" data-id="element-2185">
                  <div data-id="element-2186">
                    <label className="block text-sm font-semibold text-slate-700 mb-1.5" data-id="element-2187">
                      Campaign Name
                    </label>
                    <div className="w-full px-5 py-3.5 bg-white rounded-2xl text-base shadow-sm text-slate-700" data-id="element-2188">
                      CA Dentists Outreach
                    </div>
                  </div>
                  <div data-id="element-2189">
                    <label className="block text-sm font-semibold text-slate-700 mb-1.5" data-id="element-2190">
                      Sender Account
                    </label>
                    <div className="w-full px-5 py-3.5 bg-white rounded-2xl text-base shadow-sm text-slate-700" data-id="element-2191">
                      alex@yourcompany.com
                    </div>
                  </div>
                </div>
                <div className="mt-8 flex items-center justify-center gap-2 py-4 text-sm font-semibold" data-id="element-2192">
                  {sequencePhase === 'loading' ? <span className="flex items-center gap-2 text-blue-600" data-id="element-2193">
                      <motion.div animate={{
                  rotate: 360
                }} transition={{
                  repeat: Infinity,
                  duration: 1,
                  ease: 'linear'
                }} className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full" data-id="element-2194" />
                      Launching sequence...
                    </span> : <span className="flex items-center gap-2 text-green-600" data-id="element-2195">
                      <CheckCircle2Icon className="w-4 h-4" data-id="element-2196" />
                      Sequence active
                    </span>}
                </div>
              </div>
              <div className="lg:col-span-8 bg-slate-50 rounded-[2.5rem] p-8 sm:p-10 min-h-[420px]" data-id="element-2197">
                {sequencePhase === 'loading' && <div className="h-full min-h-[350px] flex flex-col items-center justify-center" data-id="element-2198">
                    <motion.div animate={{
                rotate: 360
              }} transition={{
                repeat: Infinity,
                duration: 1,
                ease: 'linear'
              }} className="w-10 h-10 rounded-full mb-4" style={{
                borderWidth: '3px',
                borderColor: '#e2e8f0',
                borderTopColor: '#156ef6',
                borderStyle: 'solid'
              }} data-id="element-2199" />
                    <p className="text-lg font-medium text-slate-500" data-id="element-2200">
                      Starting email sequence...
                    </p>
                  </div>}
                {sequencePhase === 'done' && <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              duration: 0.4
            }} className="min-h-[350px]" data-id="element-2201">
                    {/* Sequence Builder Header */}
                    <div className="flex items-center justify-between mb-5" data-id="element-2202">
                      <div className="flex items-center gap-3" data-id="element-2203">
                        <h3 className="text-base font-bold text-slate-900" data-id="element-2204">
                          CA Dentists Outreach
                        </h3>
                        <span className="text-xs font-medium text-slate-400 bg-slate-100 px-2.5 py-1 rounded-full" data-id="element-2205">
                          Draft
                        </span>
                      </div>
                      <div className="flex items-center gap-2" data-id="element-2206">
                        <button className="px-3 py-1.5 text-xs font-semibold text-slate-600 bg-white border border-slate-200 rounded-lg" data-id="element-2207">
                          Save draft
                        </button>
                        <a href="http://app.openmart.com/register" target="_blank" rel="noopener noreferrer" className="px-3 py-1.5 text-xs font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-1.5" data-id="element-2208">
                          <SendIcon className="w-3 h-3" data-id="element-2209" />
                          Start sequence
                        </a>
                      </div>
                    </div>

                    <div className="flex gap-4" data-id="element-2210">
                      {/* Left: Sequence Steps */}
                      <div className="w-48 shrink-0" data-id="element-2211">
                        <div className="text-[10px] font-bold text-slate-400 tracking-wider uppercase mb-3" data-id="element-2212">
                          Sequence steps
                        </div>
                        <div className="space-y-2" data-id="element-2213">
                          <div className="bg-white border-2 border-blue-200 rounded-xl p-3" data-id="element-2214">
                            <div className="text-xs font-bold text-blue-600" data-id="element-2215">
                              Initial outreach
                            </div>
                            <div className="text-[10px] text-slate-400 mt-0.5 truncate" data-id="element-2216">
                              Quick question about {'{'} company_...{'}'}
                            </div>
                          </div>
                          <div className="flex justify-center" data-id="element-2217">
                            <div className="w-px h-3 bg-slate-200" data-id="element-2218"></div>
                          </div>
                          <div className="bg-white border border-slate-200 rounded-xl p-3" data-id="element-2219">
                            <div className="text-xs font-semibold text-slate-700" data-id="element-2220">
                              Follow up 1
                            </div>
                            <div className="text-[10px] text-slate-400 mt-0.5 truncate" data-id="element-2221">
                              Re: Quick question about {'{'} compa...{'}'}
                            </div>
                            <div className="flex items-center gap-1 mt-1.5 text-[10px] text-slate-400" data-id="element-2222">
                              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-2223">
                                <circle cx="12" cy="12" r="10" strokeWidth="2" data-id="element-2224" />
                                <path strokeWidth="2" d="M12 6v6l4 2" data-id="element-2225" />
                              </svg>
                              Wait 5 days
                            </div>
                          </div>
                          <div className="flex justify-center" data-id="element-2226">
                            <div className="w-px h-3 bg-slate-200" data-id="element-2227"></div>
                          </div>
                          <div className="border border-dashed border-slate-300 rounded-xl p-2.5 text-center" data-id="element-2228">
                            <span className="text-[10px] font-medium text-slate-400" data-id="element-2229">
                              + Add step
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Right: Email Editor */}
                      <div className="flex-1 bg-white rounded-xl border border-slate-200 overflow-hidden" data-id="element-2230">
                        {/* From / To */}
                        <div className="border-b border-slate-100 px-4 py-2.5 flex items-center gap-3 text-xs" data-id="element-2231">
                          <span className="text-slate-400 w-8" data-id="element-2232">From:</span>
                          <span className="text-blue-600 font-medium" data-id="element-2233">
                            + Connect your email
                          </span>
                        </div>
                        <div className="border-b border-slate-100 px-4 py-2.5 flex items-center gap-3 text-xs" data-id="element-2234">
                          <span className="text-slate-400 w-8" data-id="element-2235">To:</span>
                          <span className="text-slate-500" data-id="element-2236">
                            4,210 contacts from CA Dentists
                          </span>
                        </div>
                        {/* Subject */}
                        <div className="border-b border-slate-100 px-4 py-2.5" data-id="element-2237">
                          <span className="text-sm font-semibold text-slate-800" data-id="element-2238">
                            Quick question about {'{'} company_name{'}'}
                          </span>
                        </div>
                        {/* Toolbar */}
                        <div className="border-b border-slate-100 px-4 py-2 flex items-center gap-3 text-slate-400" data-id="element-2239">
                          <span className="font-bold text-xs" data-id="element-2240">B</span>
                          <span className="italic text-xs" data-id="element-2241">I</span>
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-2242">
                            <path strokeWidth="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" data-id="element-2243" />
                          </svg>
                          <div className="w-px h-4 bg-slate-200 mx-1" data-id="element-2244"></div>
                          <span className="text-[10px] font-medium text-slate-500" data-id="element-2245">
                            {'{ }'} Insert variable
                          </span>
                        </div>
                        {/* Body */}
                        <div className="px-4 py-4 text-xs text-slate-600 leading-relaxed space-y-3" data-id="element-2246">
                          <p data-id="element-2247">
                            Hi {'{'} first_name{'}'},
                          </p>
                          <p data-id="element-2248">
                            I came across {'{'} company_name{'}'} and was
                            impressed by what you are doing in your industry.
                          </p>
                          <p data-id="element-2249">
                            I would love to connect and explore how we might be
                            able to help you streamline your operations and
                            drive more growth.
                          </p>
                          <p data-id="element-2250">
                            Would you be open to a quick 15-minute call this
                            week?
                          </p>
                          <p data-id="element-2251">
                            Best,
                            <br data-id="element-2252" />
                            {'{'} sender_name{'}'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </motion.div>}
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </section>;
}